/**
 * The header, the mobile drawer and the footer — shared by every public page.
 *
 * Extracted out of `index.ts` when News arrived: the landing page, the news
 * index and a news article all wear the same chrome, and three copies of a
 * nav whose items must agree with each other is the drift this file exists to
 * prevent.
 *
 * ─── Anchors and pages are different things ─────────────────────────────────
 * Janeks: *"Menu items (header and footer) are anchors to scroll to. Not
 * separate pages. The only new pages are - News."* So the nav has two kinds of
 * item and the distinction is explicit in `NAV_PAGES` vs `SECTIONS`, not left
 * to whether a href happens to start with a `#`:
 *
 *   - a SECTION item is `#id` and scrolls, and only exists on the landing page
 *     — from `/news` it has to become `/#id` or it scrolls to nothing;
 *   - a PAGE item is a route and is always a route.
 *
 * `main.ts`'s delegated click handler now leaves same-page hash links alone
 * for exactly this reason; before that it swallowed them and re-rendered the
 * whole page, so the menu appeared to do nothing.
 */
import { authStore } from '../../core/state/auth-store';
import { getSiteInfo } from '../../core/preview/preview';
import { BRAND, SECTIONS } from './landing-copy';
import { escapeAttr, escapeHtml } from './landing-escape';
import { armStoredPlayback, registerAmbience, subscribeMuted, toggleMuted } from './landing-audio';
import { loadLegalIndex, loadSocialLinks } from './landing-cms';
import { legalPath } from './legal-paths';
import {
  ICON_CLOSE, ICON_MENU, ICON_PLAY, ICON_SOUND_OFF, ICON_SOUND_ON,
} from './landing-icons';

/**
 * The illustrated wordmark, shared by the header, the footer and the auth
 * pages' own header (`auth-chrome.ts`) — every spot that used to carry
 * `LOGO_MARK` plus the brand name/settlement as separate text now carries
 * this instead, since the badge already has both baked into the art.
 * `/landing/logo-compact.webp` is a pre-sized (300x131) export of the full
 * `/landing/logo-hero.webp` badge the hero section uses — the header never
 * needs to ship hero-resolution bytes for a 40px-tall mark.
 */
export function brandLogo(): string {
  return `<img class="lp-brand-logo" src="/landing/logo-compact.webp"`
    + ` alt="${escapeAttr(BRAND.name)} — ${escapeAttr(BRAND.settlement)}" width="150" height="65">`;
}

/** Nav items that are real routes rather than in-page anchors. */
export const NAV_PAGES: { href: string; label: string }[] = [
  { href: '/news', label: 'News' },
];

export interface ChromeOptions {
  /**
   * True on the landing page, where the section anchors are on this very
   * document and a bare `#id` scrolls. Everywhere else they have to point back
   * at the landing page first.
   */
  onLanding: boolean;
  navigate: (to: string) => void;
}

/** Where a section anchor points from this page. */
function sectionHref(id: string, onLanding: boolean): string {
  return onLanding ? `#${id}` : `/#${id}`;
}

function navLinks(onLanding: boolean): string {
  return [
    ...SECTIONS.map((s) => `<a href="${sectionHref(s.id, onLanding)}">${escapeHtml(s.nav)}</a>`),
    ...NAV_PAGES.map((p) => `<a href="${escapeAttr(p.href)}">${escapeHtml(p.label)}</a>`),
  ].join('');
}

export function headerMarkup(onLanding: boolean): string {
  const signedIn = Boolean(authStore.get().user);
  const { playHref, playLabel } = playCta();
  return `
  <header class="lp-header"${onLanding ? '' : ' data-stuck="true"'}>
    <a class="lp-brand" href="/" aria-label="${escapeAttr(BRAND.name)} — home">
      ${brandLogo()}
    </a>

    <nav class="lp-nav" aria-label="Sections">${navLinks(onLanding)}</nav>

    <div class="lp-header-actions">
      <button type="button" class="lp-btn lp-icon-btn" data-mute></button>
      <a class="lp-btn lp-btn-primary" href="${escapeAttr(playHref)}" data-play>
        ${ICON_PLAY}
        <span class="lp-label-wide">${escapeHtml(playLabel)}</span>
        <span class="lp-label-narrow">Play</span>
      </a>
      <button type="button" class="lp-btn lp-icon-btn lp-burger" data-burger
        aria-expanded="false" aria-controls="lp-drawer"></button>
    </div>
  </header>

  <div class="lp-drawer" id="lp-drawer" data-open="false">
    ${navLinks(onLanding)}
    ${signedIn ? '' : '<a href="/login">Sign in</a>'}
  </div>`;
}

/** What the call to action says and where it goes, for whoever is looking at it. */
export function playCta(): { playHref: string; playLabel: string } {
  const signedIn = Boolean(authStore.get().user);
  return {
    playHref: signedIn ? '/play' : '/register',
    playLabel: signedIn ? 'Continue' : 'Play free',
  };
}

export function footerMarkup(onLanding: boolean): string {
  const year = new Date().getFullYear();
  const company = getSiteInfo()?.company;

  return `
  <footer class="lp-footer">
    <div class="lp-footer-inner">
      <div>
        <a class="lp-brand" href="/" aria-label="${escapeAttr(BRAND.name)} — home">
          ${brandLogo()}
        </a>
        <!-- The operator's registered details, from /admin. Janeks: *"under it
             show LLC data from admin, ditch the current text about game."*
             Every field is blank on a fresh deployment and the whole block is
             then omitted rather than rendering empty rows — see companyBlock. -->
        ${companyBlock(company)}
        <div class="lp-header-actions" style="margin-top:14px">
          <button type="button" class="lp-btn lp-icon-btn" data-mute></button>
        </div>
      </div>

      <div>
        <h3>Game</h3>
        <!-- Sections only. Janeks: *"remove 'sign in', remove 'play free'."*
             Both are already in the header, on every page, and a footer that
             repeats the call to action is asking twice rather than louder.
             Two columns on desktop — see .lp-footer-links-split. -->
        <nav class="lp-footer-links lp-footer-links-split" aria-label="Sections">
          ${navLinks(onLanding)}
        </nav>
      </div>

      <!-- Both blocks stay hidden until their fetch comes back with something.
           The endpoints behind them fail closed and a preview build empties
           them on purpose, so an empty heading over an empty list is the
           normal case, not an edge one. -->
      <div>
        <div data-legal-block hidden>
          <h3>Legal</h3>
          <nav class="lp-footer-links" data-legal-links aria-label="Legal"></nav>
        </div>
        <div data-social-block hidden style="margin-top:22px">
          <h3>Elsewhere</h3>
          <div class="lp-socials" data-social-links></div>
        </div>
      </div>
    </div>

    <div class="lp-colophon">
      <span>&copy; ${year} ${escapeHtml(company?.name || BRAND.name)}</span>
      <span>${escapeHtml(company?.email || '')}</span>
    </div>
  </footer>`;
}

type Company = NonNullable<ReturnType<typeof getSiteInfo>>['company'];

/**
 * The registered-entity block under the footer logo.
 *
 * Renders only the fields that have a value, and nothing at all when none do.
 * `company.*` lives in `game_settings` and ships BLANK — core bootstraps the
 * eight keys as empty strings and no admin page edits them yet, so
 * a fresh deployment has no operator identity to print. Inventing one is not
 * an option: `prisma/content/legal-pages.ts` refuses to do it for exactly the
 * reason that applies here, that a made-up company reads authoritative and is
 * false. An operator fills them in through `PUT /api/admin/settings/company`.
 */
function companyBlock(company: Company | undefined): string {
  if (!company) return '';
  const lines = [
    company.name,
    company.registration ? `Reg. ${company.registration}` : '',
    company.address,
    [company.jurisdiction, company.country].filter(Boolean).join(', '),
    company.email,
  ].filter((l) => l.trim() !== '');
  if (lines.length === 0) return '';
  return `<address class="lp-company">${lines.map((l) => `<span>${escapeHtml(l)}</span>`).join('')}</address>`;
}

/**
 * Wires the chrome that markup describes: the drawer, the mute buttons, the
 * Play links, the sticky header and the footer's two CMS-fed blocks.
 *
 * Returns a teardown. Safe to call on a page with no header (the news article
 * has one, but a future page might not) — every lookup is optional.
 */
export function wireChrome(page: HTMLElement, opts: ChromeOptions): () => void {
  const cleanups: Array<() => void> = [];
  const { playHref } = playCta();

  // ── the header goes solid once past the hero ──────────────────────────────
  // Only on the landing page: everywhere else it starts solid, because there
  // is no full-bleed image under it for a gradient wash to sit on.
  const header = page.querySelector<HTMLElement>('.lp-header');
  if (header && opts.onLanding) {
    const onScroll = (): void => {
      header.dataset.stuck = String(window.scrollY > window.innerHeight * 0.6);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    cleanups.push(() => window.removeEventListener('scroll', onScroll));
    onScroll();
  }

  // ── mobile drawer ─────────────────────────────────────────────────────────
  const drawer = page.querySelector<HTMLElement>('.lp-drawer');
  const burger = page.querySelector<HTMLButtonElement>('[data-burger]');
  if (drawer && burger) {
    const setDrawer = (open: boolean): void => {
      drawer.dataset.open = String(open);
      burger.innerHTML = open ? ICON_CLOSE : ICON_MENU;
      burger.setAttribute('aria-expanded', String(open));
      burger.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
    };
    const onBurger = (): void => setDrawer(drawer.dataset.open !== 'true');
    burger.addEventListener('click', onBurger);
    // Any link inside closes it — otherwise the anchor scrolls the page behind
    // a panel that is still covering it.
    const onDrawerClick = (e: Event): void => {
      if ((e.target as HTMLElement).closest('a')) setDrawer(false);
    };
    drawer.addEventListener('click', onDrawerClick);
    // A resize past the breakpoint leaves the drawer open over a header that
    // no longer has a button to close it.
    const wide = window.matchMedia('(min-width: 900px)');
    const onWide = (): void => { if (wide.matches) setDrawer(false); };
    wide.addEventListener('change', onWide);
    cleanups.push(
      () => burger.removeEventListener('click', onBurger),
      () => drawer.removeEventListener('click', onDrawerClick),
      () => wide.removeEventListener('change', onWide),
    );
    setDrawer(false);
  }

  // ── mute ──────────────────────────────────────────────────────────────────
  const muteButtons = [...page.querySelectorAll<HTMLButtonElement>('[data-mute]')];
  cleanups.push(subscribeMuted((muted) => {
    for (const btn of muteButtons) {
      btn.innerHTML = muted ? ICON_SOUND_OFF : ICON_SOUND_ON;
      btn.title = muted ? 'Sound off — the settlement is silent' : 'Sound on';
      btn.setAttribute('aria-label', btn.title);
      btn.setAttribute('aria-pressed', String(muted));
    }
  }));
  // A visitor who unmuted on an earlier visit gets their music back without
  // having to press the button twice — on their first gesture, since that is
  // the only moment a browser lets audio start. See `armStoredPlayback`.
  armStoredPlayback();
  const onMute = (): void => { toggleMuted(); };
  for (const btn of muteButtons) btn.addEventListener('click', onMute);
  cleanups.push(() => {
    for (const btn of muteButtons) btn.removeEventListener('click', onMute);
    registerAmbience(null);
  });

  // ── Play ──────────────────────────────────────────────────────────────────
  // Real anchors, so they keep middle-click and "open in new tab"; the SPA
  // router's own delegated handler would normally take them, but this module
  // is mounted by that router and must not assume it is listening.
  //
  // `stopPropagation` is load-bearing, and was not always. `main.ts` has a
  // delegated click handler on `document` that navigates for ANY same-origin
  // link, so both handlers used to run — harmlessly, because both called
  // `navigate()` with the same path. They stopped being equivalent when Play
  // began starting a camera pan and deferring the navigation: this handler
  // would begin the move and the document's would navigate on the same click,
  // landing on the login form a third of the way through a transition that was
  // still running behind it.
  const onPlay = (e: Event): void => {
    e.preventDefault();
    e.stopPropagation();
    opts.navigate(playHref);
  };
  const playLinks = [...page.querySelectorAll<HTMLAnchorElement>('[data-play]')];
  for (const a of playLinks) a.addEventListener('click', onPlay);
  cleanups.push(() => { for (const a of playLinks) a.removeEventListener('click', onPlay); });

  // ── footer: policies and social links from the admin panel ────────────────
  const legalHost = page.querySelector<HTMLElement>('[data-legal-links]');
  const socialHost = page.querySelector<HTMLElement>('[data-social-links]');
  let cancelled = false;

  if (legalHost) {
    void loadLegalIndex().then((pages) => {
      if (cancelled || pages.length === 0) return;
      legalHost.innerHTML = pages
        .map((p) => `<a href="${escapeAttr(legalPath(p.slug))}">${escapeHtml(p.title)}</a>`)
        .join('');
      (legalHost.closest('[data-legal-block]') as HTMLElement | null)?.removeAttribute('hidden');
    });
  }

  if (socialHost) {
    void loadSocialLinks().then((links) => {
      if (cancelled || links.length === 0) return;
      socialHost.innerHTML = links
        .map((l) => `<a class="lp-social" href="${escapeAttr(l.url)}" target="_blank"`
          + ` rel="noopener noreferrer">${escapeHtml(l.platform)}</a>`)
        .join('');
      (socialHost.closest('[data-social-block]') as HTMLElement | null)?.removeAttribute('hidden');
    });
  }
  cleanups.push(() => { cancelled = true; });

  return () => { for (const fn of cleanups) fn(); };
}
